// src/components/AuthStep.tsx
// VERSÃO ATUALIZADA - Mantém toda lógica funcional, CSS isolado

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, CheckCircle, User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import './AuthStep.css';

interface AuthStepProps {
    onSuccess: () => void;
}

type AuthMode = 'login' | 'signup';

const AuthStep: React.FC<AuthStepProps> = ({ onSuccess }) => {
    const { login } = useAuth();
    const [mode, setMode] = useState<AuthMode>('login');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    const [signupUsername, setSignupUsername] = useState('');
    const [signupEmail, setSignupEmail] = useState('');
    const [signupPassword, setSignupPassword] = useState('');
    const [signupConfirmPassword, setSignupConfirmPassword] = useState('');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: loginEmail, password: loginPassword, platform: 'enem' })
            });

            const data = await response.json();

            if (response.ok) {
                login(data.accessToken, data.refreshToken, {
                    userId: data.userId,
                    username: data.username,
                    email: data.email || loginEmail,
                    role: data.role
                });
                setSuccess('Login realizado com sucesso');
                setTimeout(() => onSuccess(), 500);
            } else {
                setError(data.message || 'Erro ao fazer login');
            }
        } catch (err) {
            setError('Erro ao conectar ao servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (signupPassword !== signupConfirmPassword) {
            setError('As senhas não coincidem');
            return;
        }

        if (signupPassword.length < 6) {
            setError('A senha deve ter no mínimo 6 caracteres');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: signupUsername, email: signupEmail, password: signupPassword })
            });

            const data = await response.json();

            if (response.ok) {
                login(data.accessToken, data.refreshToken, {
                    userId: data.userId,
                    username: data.username,
                    email: data.email || signupEmail,
                    role: data.role
                });
                setSuccess('Conta criada com sucesso');
                setTimeout(() => onSuccess(), 500);
            } else {
                setError(data.message || 'Erro ao criar conta');
            }
        } catch (err) {
            setError('Erro ao conectar ao servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleAuth = async () => {
        setLoading(true);
        setError('');

        try {
            window.location.href = '/api/auth/google';
        } catch (err) {
            setError('Erro ao iniciar autenticação com Google');
            setLoading(false);
        }
    };

    return (
        <div className="authstep-container">
            <div className="authstep-header">
                <h2 className="authstep-title">Identificação</h2>
                <p className="authstep-description">Entre ou crie uma conta para continuar</p>
            </div>

            <div className="authstep-toggle">
                <button className={`authstep-toggle-btn ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')} type="button">
                    Entrar
                </button>
                <button className={`authstep-toggle-btn ${mode === 'signup' ? 'active' : ''}`} onClick={() => setMode('signup')} type="button">
                    Criar Conta
                </button>
            </div>

            {error && (
                <div className="authstep-message error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="authstep-message success">
                    <CheckCircle size={18} />
                    <span>{success}</span>
                </div>
            )}

            {mode === 'login' ? (
                <>
                    <form onSubmit={handleLogin} className="authstep-form">
                        <div className="authstep-form-group">
                            <label className="authstep-label">Email</label>
                            <div className="authstep-input-wrapper">
                                <Mail size={18} className="authstep-input-icon" />
                                <input type="email" className="authstep-input" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="seu@email.com" required disabled={loading} autoFocus />
                            </div>
                        </div>

                        <div className="authstep-form-group">
                            <label className="authstep-label">Senha</label>
                            <div className="authstep-input-wrapper">
                                <Lock size={18} className="authstep-input-icon" />
                                <input type={showPassword ? 'text' : 'password'} className="authstep-input" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Sua senha" required disabled={loading} />
                                <button type="button" className="authstep-toggle-password" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="authstep-submit" disabled={loading}>
                            {loading ? 'Entrando...' : 'Entrar'}
                        </button>
                    </form>

                    <div className="authstep-divider"><span>ou</span></div>

                    {/* Botão Google customizado */}
                    <button
                        type="button"
                        className="authstep-btn-google"
                        onClick={handleGoogleAuth}
                        disabled={loading}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                            <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853" />
                            <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                            <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335" />
                        </svg>
                        Continuar com Google
                    </button>
                </>
            ) : (
                <>
                    <form onSubmit={handleSignup} className="authstep-form">
                        <div className="authstep-form-group">
                            <label className="authstep-label">Nome de usuário</label>
                            <div className="authstep-input-wrapper">
                                <User size={18} className="authstep-input-icon" />
                                <input type="text" className="authstep-input" value={signupUsername} onChange={(e) => setSignupUsername(e.target.value)} placeholder="Seu nome" required disabled={loading} minLength={3} autoFocus />
                            </div>
                        </div>

                        <div className="authstep-form-group">
                            <label className="authstep-label">Email</label>
                            <div className="authstep-input-wrapper">
                                <Mail size={18} className="authstep-input-icon" />
                                <input type="email" className="authstep-input" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} placeholder="seu@email.com" required disabled={loading} />
                            </div>
                        </div>

                        <div className="authstep-form-group">
                            <label className="authstep-label">Senha</label>
                            <div className="authstep-input-wrapper">
                                <Lock size={18} className="authstep-input-icon" />
                                <input type={showPassword ? 'text' : 'password'} className="authstep-input" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} placeholder="Mínimo 6 caracteres" required disabled={loading} minLength={6} />
                                <button type="button" className="authstep-toggle-password" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div className="authstep-form-group">
                            <label className="authstep-label">Confirmar senha</label>
                            <div className="authstep-input-wrapper">
                                <Lock size={18} className="authstep-input-icon" />
                                <input type={showPassword ? 'text' : 'password'} className="authstep-input" value={signupConfirmPassword} onChange={(e) => setSignupConfirmPassword(e.target.value)} placeholder="Digite a senha novamente" required disabled={loading} minLength={6} />
                            </div>
                        </div>

                        <button type="submit" className="authstep-submit" disabled={loading}>
                            {loading ? 'Criando conta...' : 'Criar Conta'}
                        </button>
                    </form>

                    <div className="authstep-divider"><span>ou</span></div>

                    {/* Botão Google customizado */}
                    <button
                        type="button"
                        className="authstep-btn-google"
                        onClick={handleGoogleAuth}
                        disabled={loading}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                            <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853" />
                            <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                            <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335" />
                        </svg>
                        Continuar com Google
                    </button>
                </>
            )}
        </div>
    );
};

export default AuthStep;